import React, { useState, useEffect } from 'react';
import { PipelineStage, getStageColor } from '../../types/pipeline';
import { pipelineAPI } from '../../services/api';
import { useUIStore } from '../../stores/uiStore';
import Modal from '../common/Modal';

interface GateCriterion {
  id: number;
  criterion: string;
  is_required: boolean;
  sort_order: number;
}

interface AdvanceStageModalProps {
  projectId: number;
  projectName: string;
  currentStage: PipelineStage;
  nextStage: PipelineStage;
  onClose: () => void;
  onAdvanced: () => void;
}

const AdvanceStageModal: React.FC<AdvanceStageModalProps> = ({
  projectId,
  projectName,
  currentStage,
  nextStage,
  onClose,
  onAdvanced,
}) => {
  const showNotification = useUIStore((s) => s.showNotification);

  const [criteria, setCriteria] = useState<GateCriterion[]>([]);
  const [checked, setChecked] = useState<Record<number, boolean>>({});
  const [justification, setJustification] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await pipelineAPI.getGateCriteria(currentStage.numericId);
        setCriteria(res.data);
        const initial: Record<number, boolean> = {};
        res.data.forEach((c: GateCriterion) => { initial[c.id] = false; });
        setChecked(initial);
      } catch {
        // No criteria is valid — some stages may not have gate criteria
      } finally {
        setLoading(false);
      }
    })();
  }, [currentStage.numericId]);

  const requiredCriteria = criteria.filter((c) => c.is_required);
  const allRequiredMet = requiredCriteria.every((c) => checked[c.id]);
  const hasUnmetRequired = requiredCriteria.length > 0 && !allRequiredMet;
  const needsJustification = hasUnmetRequired;
  const canSubmit = !needsJustification || justification.trim().length > 0;

  const handleToggle = (id: number) => {
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const gateMet: Record<string, boolean> = {};
      criteria.forEach((c) => { gateMet[String(c.id)] = checked[c.id] || false; });

      await pipelineAPI.stageTransition(projectId, {
        to_stage_id: nextStage.numericId,
        gate_criteria_met: gateMet,
        justification: justification.trim() || undefined,
      });

      showNotification('success', `${projectName} advanced to ${nextStage.label}`);
      onAdvanced();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Stage transition failed';
      showNotification('error', msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Advance Pipeline Stage" size="lg">
      {/* Stage arrow header */}
      <div className="flex items-center justify-center gap-3 py-3 mb-4">
        <div className="text-center">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center text-white text-sm font-bold mx-auto"
            style={{ backgroundColor: currentStage.color }}
          >
            {currentStage.short}
          </div>
          <p className="text-xs text-gray-500 mt-1.5 font-medium">{currentStage.label}</p>
        </div>
        <div className="flex items-center px-2">
          <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </div>
        <div className="text-center">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center text-white text-sm font-bold mx-auto ring-2 ring-offset-2"
            style={{ backgroundColor: nextStage.color, ['--tw-ring-color' as string]: nextStage.color }}
          >
            {nextStage.short}
          </div>
          <p className="text-xs font-medium mt-1.5" style={{ color: nextStage.color }}>{nextStage.label}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Gate criteria checklist */}
          {criteria.length > 0 && (
            <div className="mb-4">
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Gate Criteria Review
              </h4>
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                {criteria.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-start gap-3 px-3.5 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={checked[c.id] || false}
                      onChange={() => handleToggle(c.id)}
                      className="mt-0.5 w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                    />
                    <span className={`text-sm flex-1 ${checked[c.id] ? 'text-gray-700' : 'text-gray-500'}`}>
                      {c.criterion}
                      {c.is_required && (
                        <span className="text-red-500 ml-0.5 font-bold" title="Required">*</span>
                      )}
                    </span>
                    {checked[c.id] && (
                      <svg className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Unmet required warning + justification */}
          {needsJustification && (
            <div className="mb-4">
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg mb-3">
                <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="text-sm text-amber-800">
                  {requiredCriteria.length - requiredCriteria.filter((c) => checked[c.id]).length} required
                  {' '}criteri{requiredCriteria.length - requiredCriteria.filter((c) => checked[c.id]).length === 1 ? 'on' : 'a'} not
                  met. Provide justification to proceed.
                </span>
              </div>
              <textarea
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                placeholder="Explain why advancing without meeting all gate criteria..."
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
              />
            </div>
          )}

          {/* No criteria message */}
          {criteria.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4 mb-4">
              No gate criteria defined for this stage transition.
            </p>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
              style={{ backgroundColor: canSubmit ? getStageColor(nextStage.id) : '#9ca3af' }}
            >
              {submitting ? 'Advancing...' : `Advance to ${nextStage.short}`}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default AdvanceStageModal;
